import { Skeleton, Tooltip } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import { budgetFormat } from "../../../utils/number/budget-format.ts"
import {
  KpiExplanation,
  emptyStateText
} from "../../../components/widgets/kpi/KpiWidget"
import type { KpiTile } from "../../../components/widgets/types"
import { TABULAR, homeColor, homeText } from "../home-theme.ts"
import {
  ClipboardCheckIcon,
  ClockIcon,
  DollarSignIcon,
  FolderIcon,
  GaugeIcon,
  IconTile,
  InfoIcon,
  ReceiptTextIcon,
  TrendingUpIcon,
  TrophyIcon
} from "./home-icons.tsx"

type Tint = { bg: string; fg: string }

// One quiet tile for the ordinary metrics; the two attention tints are kept
// for the states that genuinely mean something (delayed work, budget health).
const BRAND: Tint = { bg: homeColor.tealPale, fg: homeColor.teal }
const WARNING: Tint = { bg: homeColor.warningBg, fg: homeColor.atRisk }
const LATE: Tint = { bg: "#FBEDEF", fg: homeColor.delayed }

type Glyph = React.FC<React.SVGProps<SVGSVGElement>>

/**
 * The reference's own icon per metric. Falls back by the catalog's `icon`
 * key so a tile the backend adds later still gets a sensible glyph.
 */
const BY_METRIC: Record<string, { icon: Glyph; tint: Tint }> = {
  active_projects: { icon: FolderIcon, tint: BRAND },
  delayed_projects: { icon: ClockIcon, tint: LATE },
  pending_approvals: { icon: ClipboardCheckIcon, tint: BRAND },
  budget_health: { icon: GaugeIcon, tint: WARNING },
  revenue: { icon: DollarSignIcon, tint: BRAND },
  revenue_in_window: { icon: DollarSignIcon, tint: BRAND },
  outstanding_receivables: { icon: ReceiptTextIcon, tint: BRAND },
  gross_margin: { icon: TrendingUpIcon, tint: BRAND },
  win_rate: { icon: TrophyIcon, tint: BRAND }
}

const BY_ICON_KEY: Record<string, Glyph> = {
  briefcase: FolderIcon,
  clock: ClockIcon,
  check: ClipboardCheckIcon,
  gauge: GaugeIcon,
  currency: DollarSignIcon,
  receipt: ReceiptTextIcon,
  wallet: ReceiptTextIcon,
  trending: TrendingUpIcon,
  trophy: TrophyIcon,
  target: TrophyIcon
}

export function tileGlyph(tile: Pick<KpiTile, "id"> & { icon?: string }) {
  const byMetric = BY_METRIC[tile.id]
  if (byMetric) return byMetric
  const byKey = tile.icon ? BY_ICON_KEY[tile.icon] : undefined
  return { icon: byKey ?? TrendingUpIcon, tint: BRAND }
}

const SCALE_MAX = 100

/** Percent tiles are the only ones whose range is knowable client-side. */
function percentScale(tile: KpiTile): number | null {
  if (tile.unit !== "percent") return null
  const n = Number(tile.value)
  if (!Number.isFinite(n)) return null
  return Math.max(0, Math.min(SCALE_MAX, n))
}

const format = (tile: KpiTile, currency: string): string => {
  const n = Number(tile.value)
  if (!Number.isFinite(n)) return `${tile.value}`
  if (tile.unit === "percent") return `${n.toFixed(1)}%`
  if (tile.unit === "currency") return budgetFormat(n, { currency })
  return `${tile.value}`
}

export const HomeMetricTileSkeleton = () => (
  <div style={{ padding: "16px 16px" }}>
    <Skeleton.Node active style={{ width: "100%", height: 52 }} />
  </div>
)

/**
 * One cell of the unified Portfolio Overview grid — no card of its own, which
 * is the whole point: eight outlined boxes inside an outlined section is what
 * made the previous pass read as monotonous.
 */
export const HomeMetricTile = ({
  tile,
  currency = "USD"
}: {
  tile: KpiTile
  currency?: string
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { icon, tint } = tileGlyph(tile)
  const noData = tile.has_data === false
  const scale = percentScale(tile)
  const clickable = Boolean(tile.drill_to)
  const portfolioWide = Boolean(tile.filters_ignored?.length)
  const hasExplanation = Boolean(
    tile.description || tile.formula || portfolioWide
  )

  return (
    <div
      data-testid="kpi-card"
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={clickable ? () => navigate(tile.drill_to) : undefined}
      onKeyDown={
        clickable
          ? (event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault()
                navigate(tile.drill_to)
              }
            }
          : undefined
      }
      style={{
        display: "flex",
        gap: 12,
        alignItems: "center",
        padding: "16px 16px",
        minWidth: 0,
        cursor: clickable ? "pointer" : "default"
      }}
    >
      <IconTile
        icon={icon}
        size={42}
        iconSize={23}
        background={tint.bg}
        color={tint.fg}
      />
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 5,
          flex: 1,
          minWidth: 0,
          containerType: "inline-size"
        }}
      >
        <span
          style={{
            display: "flex",
            alignItems: "center",
            gap: 5,
            // No uppercase transform: the label reads as the catalog sends
            // it, which is the sentence case the reference shows.
            ...homeText.label,
            color: homeColor.inkMuted
          }}
        >
          {/* Wraps rather than truncating: four columns leave ~175px, and
              "Outstanding Receivables" has to stay readable in it. */}
          <span style={{ minWidth: 0 }}>{tile.title}</span>
          {hasExplanation ? (
            <Tooltip
              title={<KpiExplanation tile={tile} />}
              placement="top"
              trigger={["hover", "focus"]}
            >
              <button
                type="button"
                data-testid="kpi-info"
                aria-label={t(
                  "portfolio.tile.infoLabel",
                  "How this is calculated"
                )}
                onClick={(event) => event.stopPropagation()}
                style={{
                  display: "inline-flex",
                  flex: "none",
                  cursor: "help",
                  padding: 0,
                  border: "none",
                  background: "none",
                  color: homeColor.inkMuted,
                  lineHeight: 0
                }}
              >
                <InfoIcon width={13} height={13} />
              </button>
            </Tooltip>
          ) : null}
        </span>

        {noData ? (
          <span style={{ ...homeText.body, color: homeColor.inkMuted }}>
            {emptyStateText(tile, t)}
          </span>
        ) : (
          <span
            data-testid="kpi-value"
            title={format(tile, currency)}
            style={{
              ...homeText.metricValue,
              ...TABULAR,
              // A long currency reading steps down inside its own cell
              // instead of ellipsizing to "$24,4…".
              fontSize: `clamp(19px, 100cqi / ${Math.max(
                format(tile, currency).length,
                1
              )} * 1.72, 27px)`,
              color: homeColor.ink,
              letterSpacing: "-0.01em",
              whiteSpace: "nowrap"
            }}
          >
            {format(tile, currency)}
          </span>
        )}

        {!noData && scale !== null ? (
          <div
            data-testid="kpi-scale"
            data-scale-value={scale}
            aria-hidden
            style={{
              height: 6,
              borderRadius: 999,
              background: homeColor.progressTrack,
              overflow: "hidden"
            }}
          >
            <div
              style={{
                width: `${scale}%`,
                height: "100%",
                borderRadius: 999,
                // Budget Health is the one scale whose low end is a warning,
                // and the reference colours it so.
                background:
                  tint.fg === homeColor.atRisk
                    ? homeColor.atRisk
                    : homeColor.accent
              }}
            />
          </div>
        ) : null}
      </div>
    </div>
  )
}
